import { useCallback, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import type { CurveType } from '@mui/x-charts';
import { useAppSelector } from '../../../redux/ReduxHooks';
import { stableJsonSignature } from '../../../utils/stableJsonSignature';
import type { DashboardWidgetContent } from '../dashboardTypes';
import { useDashboardCapabilities } from '../dashboardCapabilities';
import { persistDashboardLayoutToProject, updateWidgetInDashboardLayout } from '../persistDashboardWidgetContent';

export type PersistDashboardWidgetContentPatch = {
  curve?: CurveType;
  style?: string;
  sensorKeys?: string[];
  selectedMotorPorts?: number[];
  selectedMotorPort?: number;
  selectedMotorMetrics?: string[];
  selectedSensorPorts?: number[];
  sensorClassName?: string;
  countdownSeconds?: number;
};

function isWidgetDataSelectionPatch(patch: PersistDashboardWidgetContentPatch): boolean {
  return (
    patch.sensorKeys !== undefined ||
    patch.selectedMotorPorts !== undefined ||
    patch.selectedMotorPort !== undefined ||
    patch.selectedMotorMetrics !== undefined ||
    patch.selectedSensorPorts !== undefined ||
    patch.sensorClassName !== undefined
  );
}

/** Persists dashboard widget layout `content` JSON for one grid widget (live mode only). */
export function usePersistDashboardWidgetContent(widgetId: string) {
  const dispatch = useDispatch();
  const dashboardLayout = useAppSelector((state) => state.project.loadedProject?.dashboardLayout);
  const dashboardLayoutRef = useRef(dashboardLayout);
  dashboardLayoutRef.current = dashboardLayout;
  const dashboardLayoutSignature = useMemo(() => stableJsonSignature(dashboardLayout ?? []), [dashboardLayout]);
  const { canPersistWidgetStyle, canPersistWidgetDataSelection } = useDashboardCapabilities();

  const persistWidgetContent = useCallback(
    (patch: PersistDashboardWidgetContentPatch) => {
      if (!canPersistWidgetStyle) return;
      if (isWidgetDataSelectionPatch(patch) && !canPersistWidgetDataSelection) return;
      if (patch.countdownSeconds !== undefined && !canPersistWidgetDataSelection) return;
      const layout = dashboardLayoutRef.current;
      if (!layout) return;

      const mergeContent = (content: DashboardWidgetContent): DashboardWidgetContent => ({
        ...content,
        ...(patch.curve !== undefined ? { curve: patch.curve } : {}),
        ...(patch.style !== undefined ? { style: patch.style } : {}),
        ...(patch.sensorKeys !== undefined ? { sensorKeys: patch.sensorKeys } : {}),
        options: {
          ...content.options,
          ...(patch.selectedMotorPorts !== undefined ? { selectedMotorPorts: patch.selectedMotorPorts } : {}),
          ...(patch.selectedMotorPort !== undefined ? { selectedMotorPort: patch.selectedMotorPort } : {}),
          ...(patch.selectedMotorMetrics !== undefined ? { selectedMotorMetrics: patch.selectedMotorMetrics } : {}),
          ...(patch.selectedSensorPorts !== undefined ? { selectedSensorPorts: patch.selectedSensorPorts } : {}),
          ...(patch.sensorClassName !== undefined ? { sensorClassName: patch.sensorClassName } : {}),
          ...(patch.countdownSeconds !== undefined ? { countdownSeconds: patch.countdownSeconds } : {}),
        },
      });

      const nextLayout = updateWidgetInDashboardLayout(layout, widgetId, mergeContent);
      if (!nextLayout) return;

      persistDashboardLayoutToProject(dispatch, nextLayout);
    },
    // dashboardLayoutSignature invalidates when layout content changes; layout is read from dashboardLayoutRef
    // eslint-disable-next-line react-hooks/exhaustive-deps -- layout read from ref
    [canPersistWidgetDataSelection, canPersistWidgetStyle, dashboardLayoutSignature, dispatch, widgetId]
  );

  return { persistWidgetContent };
}
